import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { api, API } from "@/lib/api";
import { productUrl } from "@/lib/productUrl";
import { track } from "@/lib/analytics";
import { Sparkles, ArrowRight, ArrowLeft, Check, RotateCcw } from "lucide-react";

const QUESTIONS = [
  {
    id: "event",
    title: "What are we dressing for?",
    sub: "Every event has its own vibe.",
    options: [
      { v: "prom", label: "Prom", emoji: "👑" },
      { v: "homecoming", label: "Homecoming", emoji: "🎓" },
      { v: "graduation", label: "Graduation", emoji: "💎" },
      { v: "other", label: "Another formal", emoji: "🥂" },
    ],
  },
  {
    id: "vibe",
    title: "What's your vibe?",
    sub: "Pick the one that feels most you.",
    options: [
      { v: "romantic", label: "Romantic & soft", emoji: "🌸" },
      { v: "bold", label: "Bold & unforgettable", emoji: "🔥" },
      { v: "classic", label: "Timeless & classic", emoji: "🤍" },
      { v: "edgy", label: "Edgy & unexpected", emoji: "⚡" },
    ],
  },
  {
    id: "silhouette",
    title: "Silhouette love?",
    sub: "You can pick more than one.",
    multi: true,
    options: [
      { v: "ballgown", label: "Ballgown drama", emoji: "👗" },
      { v: "mermaid", label: "Fit & flare", emoji: "🧜🏽‍♀️" },
      { v: "aline", label: "A-line ease", emoji: "🌷" },
      { v: "short", label: "Short & flirty", emoji: "🥂" },
    ],
  },
  {
    id: "palette",
    title: "Your color story?",
    sub: "One or two — we'll style around it.",
    multi: true,
    options: [
      { v: "pink", label: "Pink & rose", emoji: "🌹" },
      { v: "black", label: "Black & smoke", emoji: "🖤" },
      { v: "jewel", label: "Emerald / Sapphire / Ruby", emoji: "💚" },
      { v: "nude", label: "Nude / Champagne / Ivory", emoji: "🥂" },
      { v: "bright", label: "Bright color pop", emoji: "🎨" },
      { v: "silver", label: "Silver / Metallic", emoji: "✨" },
    ],
  },
  {
    id: "length",
    title: "How long do you want to go?",
    options: [
      { v: "floor", label: "Floor-length only", emoji: "👑" },
      { v: "midi", label: "Midi / knee-length", emoji: "🌸" },
      { v: "short", label: "Short & flirty", emoji: "💃" },
      { v: "any", label: "Show me everything", emoji: "🎀" },
    ],
  },
  {
    id: "budget",
    title: "Comfort zone?",
    sub: "We'll find something perfect at your number.",
    options: [
      { v: 400, label: "Under $400", emoji: "💫" },
      { v: 600, label: "$400 – $600", emoji: "💎" },
      { v: 900, label: "$600 – $900", emoji: "👑" },
      { v: 9999, label: "Show me couture", emoji: "🌹" },
    ],
  },
];

// Map answers → filters
function toFilters(a) {
  const category = a.event === "prom" ? "prom-dresses" : a.event === "homecoming" ? "homecoming-dresses" : "prom-dresses";
  const palette = Array.isArray(a.palette) ? a.palette : [];
  const colorKeywords = [];
  if (palette.includes("pink")) colorKeywords.push("pink", "rose", "blush");
  if (palette.includes("black")) colorKeywords.push("black", "smoke");
  if (palette.includes("jewel")) colorKeywords.push("emerald", "sapphire", "ruby");
  if (palette.includes("nude")) colorKeywords.push("nude", "champagne", "ivory");
  if (palette.includes("bright")) colorKeywords.push("red", "yellow", "coral");
  if (palette.includes("silver")) colorKeywords.push("silver", "metallic");
  return {
    category,
    maxPrice: a.budget || 9999,
    colorKeywords,
    silhouettes: Array.isArray(a.silhouette) ? a.silhouette : [],
    length: a.length,
    vibe: a.vibe,
  };
}

function scoreProduct(p, f) {
  let s = 0;
  const c = (p.color || "").toLowerCase();
  const n = (p.name || "").toLowerCase();
  const t = ((p.tags || []).join(" ")).toLowerCase();
  const hay = `${c} ${n} ${t}`;
  for (const k of f.colorKeywords) if (hay.includes(k)) s += 3;
  for (const sil of f.silhouettes) if (hay.includes(sil)) s += 2;
  if (f.length === "floor" && (hay.includes("gown") || hay.includes("long"))) s += 1;
  if (f.length === "short" && (hay.includes("mini") || hay.includes("short"))) s += 1;
  if (f.vibe === "bold" && (hay.includes("sequin") || hay.includes("bold") || hay.includes("cutout"))) s += 1;
  if (f.vibe === "romantic" && (hay.includes("floral") || hay.includes("lace") || hay.includes("ruffle"))) s += 1;
  if (f.vibe === "classic" && (hay.includes("classic") || hay.includes("satin") || hay.includes("simple"))) s += 1;
  if ((p.price || 0) <= f.maxPrice) s += 0.5;
  if (p.trend) s += 0.3;
  return s;
}

export default function StyleMatch() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const q = QUESTIONS[step];
  const done = step >= QUESTIONS.length;
  const progress = Math.min(100, ((step) / QUESTIONS.length) * 100);

  const pick = (val) => {
    if (q.multi) {
      const cur = answers[q.id] || [];
      const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
      setAnswers({ ...answers, [q.id]: next });
    } else {
      setAnswers({ ...answers, [q.id]: val });
      setTimeout(() => setStep((s) => s + 1), 250);
    }
  };

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setLoading(true);
    const f = toFilters(answers);
    try {
      // Silo endpoint returns products by category_slug (prom-dresses / homecoming-dresses / tuxedos)
      const { data } = await api.silo(f.category);
      const raw = Array.isArray(data?.products) ? data.products : [];
      const list = raw.map((p) => ({ ...p, _score: scoreProduct(p, f) }));
      // First try to honor the budget cap
      let picks = list
        .filter((p) => (p.price || 0) > 0 && (p.price || 0) <= f.maxPrice + 100)
        .sort((a, b) => b._score - a._score)
        .slice(0, 3);
      // Fallback: relax budget and take top scored anyway so we always show 3
      if (picks.length < 3) {
        const seen = new Set(picks.map((p) => p.id));
        const rest = list
          .filter((p) => !seen.has(p.id))
          .sort((a, b) => b._score - a._score);
        picks = picks.concat(rest).slice(0, 3);
      }
      if (picks.length === 0) {
        toast.error("We couldn't load any dresses — try again in a moment.");
      }
      setResults(picks);
      track("style_match_completed", {
        event: answers.event,
        vibe: answers.vibe,
        budget: answers.budget,
        result_count: picks.length,
      });
    } catch (e) {
      toast.error("Something went wrong picking your matches. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setAnswers({}); setStep(0); setResults([]); };

  const canAdvance = q?.multi ? (answers[q?.id] || []).length > 0 : answers[q?.id] != null;
  const selected = q ? (Array.isArray(answers[q.id]) ? answers[q.id] : [answers[q.id]].filter(Boolean)) : [];

  return (
    <div data-testid="style-match" className="min-h-screen bg-cream">
      <Seo title="Style Match — Your Perfect Prom Look" description="Take our 6-question style quiz. AI matches you to your three perfect prom dresses in 60 seconds." path="/style-match" />

      <section className="max-w-[900px] mx-auto px-5 sm:px-8 pt-12 pb-24">
        <div className="text-center mb-10">
          <Sparkles className="w-8 h-8 mx-auto text-rosemetal mb-3" strokeWidth={1.5} />
          <p className="font-label text-[10px] text-rosemetal tracking-widest">STYLE MATCH · 60 SECONDS</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 leading-[0.95]">Your dream dress<br /><span className="italic text-rosemetal">in three answers.</span></h1>
        </div>

        {/* Progress bar */}
        {!done && (
          <div className="mb-8">
            <div className="flex justify-between font-label text-[9px] text-muted-foreground tracking-widest mb-2">
              <span>QUESTION {step + 1} OF {QUESTIONS.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-1 bg-blush rounded-full overflow-hidden">
              <motion.div className="h-full bg-rosemetal" initial={{ width: "0%" }} animate={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {!done && (
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35 }}
              data-testid={`quiz-step-${step}`}
            >
              <h2 className="font-display text-3xl sm:text-4xl leading-tight">{q.title}</h2>
              {q.sub && <p className="font-body text-sm text-muted-foreground mt-2">{q.sub}</p>}

              <div className={`mt-8 grid ${q.options.length > 4 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-3`}>
                {q.options.map((o) => {
                  const isPicked = q.multi ? selected.includes(o.v) : answers[q.id] === o.v;
                  return (
                    <button
                      key={o.v}
                      onClick={() => pick(o.v)}
                      data-testid={`quiz-option-${q.id}-${o.v}`}
                      className={`text-left border rounded-sm p-5 flex items-start gap-4 transition-all ${isPicked ? "border-rosemetal bg-blush/40 shadow-md" : "border-border bg-ivory hover:border-ink/40"}`}
                    >
                      <span className="text-2xl">{o.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-lg leading-tight">{o.label}</div>
                      </div>
                      {isPicked && <Check className="w-4 h-4 text-rosemetal shrink-0" />}
                    </button>
                  );
                })}
              </div>

              <div className="mt-10 flex items-center justify-between">
                <button onClick={back} disabled={step === 0} className="font-label text-[10px] text-muted-foreground tracking-widest flex items-center gap-1 disabled:opacity-30">
                  <ArrowLeft className="w-3 h-3" /> BACK
                </button>
                {q.multi && (
                  <button
                    onClick={next}
                    disabled={!canAdvance}
                    data-testid="quiz-next"
                    className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-label text-[10px] tracking-widest ${canAdvance ? "bg-ink text-cream hover:bg-rosemetal" : "bg-border text-muted-foreground cursor-not-allowed"} transition-colors`}
                  >
                    NEXT <ArrowRight className="w-3 h-3" />
                  </button>
                )}
                {step === QUESTIONS.length - 1 && !q.multi && null}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {done && results.length === 0 && !loading && (
          <div className="text-center py-8" data-testid="quiz-submit-screen">
            <p className="font-display italic text-2xl text-rosemetal">Ready to see your matches?</p>
            <button onClick={submit} className="mt-6 inline-flex items-center gap-2 bg-ink text-cream px-8 py-4 rounded-full font-label text-[10px] tracking-widest hover:bg-rosemetal transition-colors">
              REVEAL MY 3 DRESSES <Sparkles className="w-3 h-3" />
            </button>
          </div>
        )}

        {loading && (
          <div className="text-center py-16">
            <div className="inline-block w-10 h-10 border-2 border-rosemetal border-t-transparent rounded-full animate-spin" />
            <p className="font-label text-[10px] text-muted-foreground tracking-widest mt-4">FINDING YOUR PERFECT MATCH…</p>
          </div>
        )}

        {results.length > 0 && (
          <div data-testid="quiz-results" className="mt-4">
            <p className="font-label text-[10px] text-rosemetal tracking-widest text-center">YOUR STYLE MATCH</p>
            <h2 className="font-display text-4xl sm:text-5xl text-center mt-2 leading-[0.95]">Meet your <span className="italic text-rosemetal">top 3.</span></h2>
            <p className="font-body text-sm text-muted-foreground text-center mt-3 max-w-md mx-auto">
              Ranked by fit to your quiz. Tap "Try This Look" to see yourself in it — or lock it before anyone else at your school can.
            </p>
            <div className="mt-10 grid md:grid-cols-3 gap-5">
              {results.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15 }}
                  className="relative"
                  data-testid={`match-result-${i}`}
                >
                  <div className="absolute -top-3 -left-3 z-10 bg-champagne text-ink w-10 h-10 rounded-full flex items-center justify-center font-display text-xl">{i + 1}</div>
                  <Link to={productUrl(p)} className="block relative overflow-hidden bg-blush aspect-[3/4] group">
                    <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </Link>
                  <div className="mt-3">
                    <div className="font-display text-lg leading-tight">{p.name}</div>
                    <div className="font-label text-[10px] text-muted-foreground mt-1 tracking-widest">{p.color} · ${p.price}</div>
                    <div className="mt-3 flex gap-2">
                      <Link to={`${productUrl(p)}?tryon=1`} className="flex-1 text-center bg-ink text-cream px-3 py-2 font-label text-[9px] rounded-full tracking-widest hover:bg-rosemetal transition-colors">
                        TRY THIS LOOK
                      </Link>
                      <Link to={productUrl(p)} className="border border-ink text-ink px-3 py-2 font-label text-[9px] rounded-full tracking-widest hover:bg-ink hover:text-cream transition-colors">
                        VIEW
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-12 text-center">
              <button onClick={reset} className="inline-flex items-center gap-2 border border-ink px-6 py-3 rounded-full font-label text-[10px] tracking-widest hover:bg-ink hover:text-cream transition-colors">
                <RotateCcw className="w-3 h-3" /> START OVER
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
